import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Loader2, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useToast } from '@/hooks/use-toast';
import AdminLayout from '@/components/admin/AdminLayout';
import { supabase } from '@/integrations/supabase/client';

interface Category { id: string; name: string; slug: string; description: string | null; }

const AdminBlogCategories = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [newCategory, setNewCategory] = useState({ name: '', slug: '', description: '' });
  const [editForm, setEditForm] = useState({ name: '', slug: '', description: '' });
  const [isAdding, setIsAdding] = useState(false);
  const { toast } = useToast();

  useEffect(() => { fetchCategories(); }, []);

  const fetchCategories = async () => {
    const { data, error } = await supabase.from('blog_categories').select('*').order('name');
    if (error) toast({ title: 'Error', description: 'Failed to load categories', variant: 'destructive' });
    else setCategories(data || []);
    setLoading(false);
  };

  const generateSlug = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const handleAdd = async () => {
    if (!newCategory.name.trim()) { toast({ title: 'Error', description: 'Name is required', variant: 'destructive' }); return; }
    const { error } = await supabase.from('blog_categories').insert({ name: newCategory.name.trim(), slug: newCategory.slug || generateSlug(newCategory.name), description: newCategory.description.trim() || null });
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
    else { toast({ title: 'Success', description: 'Category created' }); setNewCategory({ name: '', slug: '', description: '' }); setIsAdding(false); fetchCategories(); }
  };

  const startEdit = (cat: Category) => { setEditingId(cat.id); setEditForm({ name: cat.name, slug: cat.slug, description: cat.description || '' }); };

  const handleUpdate = async () => {
    if (!editingId || !editForm.name.trim()) return;
    const { error } = await supabase.from('blog_categories').update({ name: editForm.name.trim(), slug: editForm.slug || generateSlug(editForm.name), description: editForm.description.trim() || null }).eq('id', editingId);
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
    else { toast({ title: 'Success', description: 'Category updated' }); setEditingId(null); fetchCategories(); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const { error } = await supabase.from('blog_categories').delete().eq('id', deleteId);
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
    else { toast({ title: 'Deleted', description: 'Category deleted' }); fetchCategories(); }
    setDeleteId(null);
  };

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-4xl">
        <div className="flex justify-between items-center">
          <div><h1 className="text-3xl font-bold text-foreground">Blog Categories</h1><p className="text-muted-foreground mt-1">Organize your blog posts</p></div>
          <Button onClick={() => setIsAdding(true)} disabled={isAdding}><Plus className="mr-2 h-4 w-4" />Add Category</Button>
        </div>
        {isAdding && (
          <Card>
            <CardHeader><CardTitle>New Category</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Input placeholder="Category name" value={newCategory.name} onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value, slug: generateSlug(e.target.value) }))} />
                <Input placeholder="Slug (auto-generated)" value={newCategory.slug} onChange={(e) => setNewCategory(prev => ({ ...prev, slug: e.target.value }))} />
              </div>
              <Input placeholder="Description (optional)" value={newCategory.description} onChange={(e) => setNewCategory(prev => ({ ...prev, description: e.target.value }))} />
              <div className="flex gap-2">
                <Button onClick={handleAdd}><Save className="mr-2 h-4 w-4" />Save</Button>
                <Button variant="outline" onClick={() => { setIsAdding(false); setNewCategory({ name: '', slug: '', description: '' }); }}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        )}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : categories.length === 0 ? (
          <Card><CardContent className="py-12 text-center"><p className="text-muted-foreground">No categories yet. Create your first one!</p></CardContent></Card>
        ) : (
          <div className="border rounded-lg">
            <Table>
              <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Slug</TableHead><TableHead>Description</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {categories.map((cat) => (
                  <TableRow key={cat.id}>
                    {editingId === cat.id ? (
                      <>
                        <TableCell><Input value={editForm.name} onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))} /></TableCell>
                        <TableCell><Input value={editForm.slug} onChange={(e) => setEditForm(prev => ({ ...prev, slug: e.target.value }))} /></TableCell>
                        <TableCell><Input value={editForm.description} onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))} /></TableCell>
                        <TableCell className="text-right"><div className="flex justify-end gap-2"><Button size="icon" variant="ghost" onClick={handleUpdate}><Save className="h-4 w-4" /></Button><Button size="icon" variant="ghost" onClick={() => setEditingId(null)}><X className="h-4 w-4" /></Button></div></TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell className="font-medium">{cat.name}</TableCell>
                        <TableCell className="text-muted-foreground">{cat.slug}</TableCell>
                        <TableCell className="text-muted-foreground">{cat.description || '-'}</TableCell>
                        <TableCell className="text-right"><div className="flex justify-end gap-2"><Button size="icon" variant="ghost" onClick={() => startEdit(cat)}><Edit className="h-4 w-4" /></Button><Button size="icon" variant="ghost" onClick={() => setDeleteId(cat.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button></div></TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete category?</AlertDialogTitle><AlertDialogDescription>Posts in this category will be uncategorized.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default AdminBlogCategories;
